import React, { FC, useMemo } from 'react';

import { useTranslation } from 'react-i18next';

import DownloadIcon from '../../../../assets/icons/DownloadIcon';
import LinkedInIcon from '../../../../assets/icons/LinkedInIcon';
import ShareIcon from '../../../../assets/icons/ShareIcon';
import Modal, { Modals } from '../../../../modals/Modal';
import { generateImageKitUrl } from '../../../../modules/imageKit';
import styled from '../../../../styled';
import { colorsPalette, SMALL_MARGIN_PX } from '../../../../theme';
import { generateCertificateName, getCertificatePDFUrl } from '../../../../utils/helpers';
import useDownloadFile from '../../../../utils/hooks/useDownloadFile';
import { LightTooltip } from '../../../UI/LightTooltip';

interface CardTestResultsProps {
  hash?: string | null;
  passDate?: string | null;
  pathwayName?: string | null;
  tenantTitle?: string | null;
  tenantName?: string | null;
  cpdCredits?: string | null;
  category?: string | null;
}

const CardTestResults: FC<CardTestResultsProps> = ({
  hash,
  passDate,
  pathwayName,
  tenantTitle,
  tenantName,
  cpdCredits,
  category,
}) => {
  const { t } = useTranslation();
  const { download, loading: loadingDownloadCertificate } = useDownloadFile();

  const certName = generateCertificateName(tenantTitle, pathwayName);
  const certUrl = useMemo(
    () =>
      generateImageKitUrl({
        path: getCertificatePDFUrl(hash, tenantName),
        transformation: undefined,
      }),
    [hash, tenantName],
  );

  return (
    <Wrapper data-testid="cardTestResults">
      <BtnWrapper>
        <Modal entityName={pathwayName} passDate={passDate} resultHash={hash}>
          {({ openModal }) => (
            <StyledTooltip title={t('page.pathway.addToProfile') || ''} placement="top">
              <Btn data-testid="addToProfileBtn" onClick={() => openModal(Modals.LinkedInModal)}>
                <StyledLinkedInIcon color={colorsPalette.white} />
                <BtnText>{t('page.pathway.addToProfile')}</BtnText>
              </Btn>
            </StyledTooltip>
          )}
        </Modal>
      </BtnWrapper>
      <BtnWrapper>
        {/*TODO fix after LightTooltip refactoring*/}
        <StyledTooltip title={t('page.pathway.cpdCertificate') || ''} placement="top">
          <DownloadButton
            data-testid="downloadCertificateBtn"
            onClick={() => !loadingDownloadCertificate && download(certName, certUrl)}
            disabled={loadingDownloadCertificate}
          >
            <StyledDownloadIcon color={colorsPalette.white} />
            <BtnText>{t('page.pathway.cpdCertificate')}</BtnText>
          </DownloadButton>
        </StyledTooltip>
      </BtnWrapper>
      <BtnWrapper>
        <Modal
          resultHash={hash}
          pathwayName={pathwayName}
          cpdCredits={cpdCredits}
          category={category}
        >
          {({ openModal }) => (
            <StyledTooltip title={t('page.pathway.share') || ''} placement="top">
              <Btn data-testid="openShareModalBtn" onClick={() => openModal(Modals.ShareModal)}>
                <StyledShareIcon color={colorsPalette.white} />
                <BtnText>{t('page.pathway.share')}</BtnText>
              </Btn>
            </StyledTooltip>
          )}
        </Modal>
      </BtnWrapper>
    </Wrapper>
  );
};

export default CardTestResults;

const Btn = styled('button')`
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  color: ${colorsPalette.white};
  line-height: 1;
  font-size: 18px;
  font-weight: bold;
  cursor: pointer;

  &:focus {
    outline: none;
  }
`;
const DownloadButton = styled(Btn)`
  transition: opacity 0.3s;
  opacity: ${({ disabled }) => disabled && 0.3};
  pointer-events: ${({ disabled }) => disabled && 'none'};
`;

const Wrapper = styled('div')`
  display: flex;
  flex-direction: column;
  margin-bottom: ${SMALL_MARGIN_PX};

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-bottom: 0;
    flex-direction: row;
  }
`;
const BtnWrapper = styled('div')`
  display: flex;
  align-items: center;
  justify-content: center;
  height: 47px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    &:not(:last-child) {
      padding-right: ${SMALL_MARGIN_PX};
      margin-right: ${SMALL_MARGIN_PX};
      border-right: 1px solid ${colorsPalette.violetAnalyticsCard};
    }
  }
`;
const BtnText = styled('span')`
  display: block;

  ${({ theme }) => theme.breakpoints.up('md')} {
    display: none;
  }

  ${({ theme }) => theme.breakpoints.up('xl')} {
    display: block;
  }
`;
const createStyledIcon = (tag) => styled(tag)`
  width: 21px;
  height: 21px;
  margin-right: 9px;

  ${({ theme }) => theme.breakpoints.up('md')} {
    margin-right: 0;
  }

  ${({ theme }) => theme.breakpoints.up('xl')} {
    margin-right: 9px;
  }
`;
const StyledLinkedInIcon = createStyledIcon(LinkedInIcon);
const StyledDownloadIcon = createStyledIcon(DownloadIcon);
const StyledShareIcon = createStyledIcon(ShareIcon);
const StyledTooltip = styled(LightTooltip)`
  ${({ theme }) => theme.breakpoints.up('xl')} {
    display: none;
  }
`;
